import {
  Button,
  Card,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Icon,
  Modal,
  Popover,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import calculateTaxDetails from "../../actions/calculateTaxDetails";
import calculateTaxTotals from "../../actions/calculateTaxTotals";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    minWidth: "400px",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "2rem",
  },
});

const TaxTotalsModal = ({
  open,
  setOpen,
  title,
  date,
  eventUrl,
  jobSlug,
  startDate,
  endDate,
  approximatedBurdenRate,
}) => {
  const { currentLoggedUser } = useAppContextController();

  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();

  const calculateMutation = useMutation(calculateTaxTotals, {
    onError: (e) => {
      const errorMessage =
        e?.response?.data?.message ?? "An error has occurred calculating the tax totals";
      enqueueSnackbar(errorMessage, { variant: "error" });
    },
  });

  const [taxValues, setTaxValues] = useState(null);
  const [notes, setNotes] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const notesOpen = Boolean(anchorEl);

  const requestTaxData = async () => {
    const response = await calculateMutation.mutateAsync({
      eventUrl,
      jobSlug,
      data: { startDate, endDate, approximatedBurdenRate },
    });
    if (response?.data?.data?.taxInfo) {
      setTaxValues(response.data.data.taxInfo);
    }
    if (response?.data?.data?.notes) {
      setNotes(response.data.data.notes);
    }
  };

  useEffect(() => {
    requestTaxData();
  }, []);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <Grid container>
            <Grid item>
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                borderRadius="xl"
                mr={3}
              >
                <Icon fontSize="large">paid</Icon>
              </MDBox>
            </Grid>
            <Grid item container width="unset" direction="column">
              <MDTypography variant="h5" color="dark">
                Estimated Payroll Gross Margin
              </MDTypography>
              <MDTypography variant="text" color="secondary" fontSize={16}>
                {`${title}`}
              </MDTypography>
            </Grid>
          </Grid>

          <MDBox>
            {calculateMutation.isLoading && (
              <Grid container xs={12} mt={3} direction="column" alignItems="center">
                <MDTypography variant="text" color="dark" sx={{ mr: 2 }}>
                  Calculating
                </MDTypography>
                <CircularProgress color="secondary" size={20} sx={{ marginRight: 3 }} />
              </Grid>
            )}
            {!calculateMutation.isLoading && taxValues ? (
              <Grid container xs={12} mt={3} direction="column">
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="h5" color="dark">
                    Hours Total:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`${taxValues.hoursTotal}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="text" color="dark">
                    Payroll Total:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`$${taxValues.payrollTotal}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="text" color="dark">
                    Burden Total:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`$${taxValues.burdenTotal}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="h5" color="dark">
                    Gross Payroll Total:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`$${taxValues.grossPayrollTotal}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="text" color="dark">
                    Billable Hours:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`${taxValues.billableHours}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="text" color="dark">
                    Billable Total:
                  </MDTypography>
                  <MDTypography variant="text" color="secondary">
                    {`$${taxValues.billableTotal}`}
                  </MDTypography>
                </Grid>
                <Grid container item alignItems="center">
                  <Divider sx={{ width: "100%" }} />
                </Grid>
                <Grid container item alignItems="center" gap={1}>
                  <MDTypography variant="h5" color="success">
                    Payroll Gross Margin:
                  </MDTypography>
                  <MDTypography variant="h5" color="success">
                    {`$${taxValues.payrollGrossMargin}`}
                  </MDTypography>
                </Grid>
              </Grid>
            ) : null}

            <Grid container mt={2} justifyContent="flex-end">
              <Grid container item spacing={3} justifyContent="flex-end">
                {/* <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleClick}
                    disabled={!notes?.length}
                  >
                    <MDTypography variant="body2" color="white">
                      Notes
                    </MDTypography>
                  </Button>
                  <Popover
                    id="tax-details-notes"
                    open={notesOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    PaperProps={{
                      sx: {
                        backgroundColor: "white !important",
                        borderColor: "lightgray",
                        borderWidth: 1,
                        borderStyle: "solid",
                      },
                    }}
                  >
                    {notes?.map((note, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <MDTypography variant="body2" color="dark" key={index}>
                        {note}
                      </MDTypography>
                    ))}
                  </Popover>
                </Grid> */}
                <Grid item>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={async (e) => {
                      e?.stopPropagation();
                      setOpen(false);
                    }}
                  >
                    <MDTypography variant="body2" color="white">
                      Close
                    </MDTypography>
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default TaxTotalsModal;
